/**
 * A thief's own abilities, and the points that buy them.
 *
 * **The percentage is the server's and the allocation is the player's.** What
 * shows here is base + spent + race + Dexterity + armour, assembled per read —
 * so taking off the chain mail changes the numbers on this panel, which is the
 * whole reason nothing here adds anything up.
 *
 * A skill at or below the floor is shown as **unusable rather than as 0%**,
 * because the book's answer is that it cannot be attempted at all, and a
 * player looking at "0%" would reasonably think it was worth a try.
 */

import { useState } from 'react';

export interface ThievingSkillView {
  readonly skill: string;
  readonly allocated: number;
  readonly chance: number;
  readonly usable: boolean;
  readonly terms: readonly { readonly name: string; readonly value: number }[];
  readonly unavailable?: string;
}

export interface ThievingView {
  readonly characterClass: string;
  readonly pointsSpent: number;
  readonly pointsAvailable: number;
  readonly skills: readonly ThievingSkillView[];
}

export interface ThievingSectionProps {
  readonly thieving: ThievingView;
  /** Writes the whole allocation; the server re-derives every chance from it. */
  readonly onChange: (allocations: Record<string, number>) => void;
  readonly readOnly?: boolean;
  /** A refusal of the last allocation, shown beside the button, not only at the top of the page. */
  readonly notice?: string | null;
}

export function ThievingSection({ thieving, onChange, readOnly, notice }: ThievingSectionProps) {
  const [draft, setDraft] = useState<Record<string, string>>({});

  const spend = (skill: string, value: string): void => {
    setDraft((current) => ({ ...current, [skill]: value }));
  };

  const save = (): void => {
    const allocations: Record<string, number> = {};
    for (const entry of thieving.skills) {
      const typed = draft[entry.skill];
      const points = typed === undefined ? entry.allocated : Number(typed);
      if (Number.isInteger(points) && points > 0) allocations[entry.skill] = points;
    }
    onChange(allocations);
    setDraft({});
  };

  const left = thieving.pointsAvailable - thieving.pointsSpent;

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        {thieving.characterClass === 'bard' ? 'Bardic skills' : 'Thieving skills'}
      </h2>
      {/* Roll-under on d100, and the *other* roll-under on the sheet is a d20
          against an ability score — saying which is which is cheaper than
          having somebody learn it the wrong way round. */}
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        roll d100 <strong>under</strong> the chance · {thieving.pointsSpent} of{' '}
        {thieving.pointsAvailable} points spent{left > 0 ? `, ${left} left` : ''}
      </p>

      <ul className="space-y-1 text-sm">
        {thieving.skills.map((entry) => (
          <li key={entry.skill} className="flex flex-wrap items-baseline gap-2">
            <span className="min-w-36 font-medium">{entry.skill}</span>
            <span className="tabular-nums">
              {entry.unavailable ? '—' : entry.usable ? `${entry.chance}%` : 'unusable'}
            </span>
            {/* The terms, so a number that moved has a visible reason: armour
                is in here, and a thief who cannot see why their Move Silently
                dropped fifteen points has no way to tell that from a bug. */}
            {entry.terms.length > 0 && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {entry.terms
                  .map((term) => `${term.name} ${term.value > 0 ? '+' : ''}${term.value}`)
                  .join(' · ')}
              </span>
            )}
            {entry.unavailable && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">{entry.unavailable}</span>
            )}
            {!readOnly && (
              <input
                type="number"
                min={0}
                aria-label={`Points in ${entry.skill}`}
                value={draft[entry.skill] ?? String(entry.allocated)}
                onChange={(e) => spend(entry.skill, e.target.value)}
                className="w-16 rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              />
            )}
          </li>
        ))}
      </ul>

      {!readOnly && (
        <button
          type="button"
          onClick={save}
          className="rounded-md border border-neutral-300 px-3 py-1 text-sm dark:border-neutral-700"
        >
          Spend points
        </button>
      )}
      {notice && (
        <p role="alert" className="text-xs text-amber-800 dark:text-amber-300">
          {notice}
        </p>
      )}
    </section>
  );
}
